import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

interface TimelineItem {
  id: string;
  year: string;
  title: string;
  institution: string;
  description: string[];
  type: 'work' | 'education';
  icon: string;
  color: string;
}

const timelineData: TimelineItem[] = [
  {
    id: 'exp-1',
    year: 'February 2025 – February 2026',
    title: 'Developer & Security Intern',
    institution: 'Meehaan Pvt. Ltd, Pune, India',
    description: [
      'Completed a paid offline internship, developing and securing a product-selling and marketing website for Meehaan Enterprises to support sales across India and international markets.',
      'Implemented data protection measures to secure sensitive user information.',
      'Added new features on a daily basis and ensured website stability.',
    ],
    type: 'work',
    icon: '💻',
    color: 'var(--neon-green)',
  },
  {
    id: 'edu-1',
    year: '2023 – 2026',
    title: 'B.Tech in Computer Science',
    description: [
      'Currently pursuing a Bachelor of Technology degree with a focus on computer engineering.',
      'Gaining strong foundational knowledge in data structures, algorithms, and Web Security development principles.',
      'Participating in various technical workshops to enhance skills.',
      'Collaborating with peers on academic projects and contributing to open-source initiatives.',
    ],
    type: 'education',
    icon: '🎓',
    color: 'var(--neon-cyan)',
  },
  {
    id: 'edu-2',
    year: '2020 – 2023',
    title: 'Diploma In (Computer Science)',
    institution: 'Dr. D. Y. Patil Pratishthan, Akurdi, Pune',
    description: [
      'Completed Diploma education with a focus on core computer subjects (OS, Java, Python).',
      'Achieved strong academic performance throughout the coursework.',
      'Participated in extracurricular activities and hands-on Coding Labs.',
    ],
    type: 'education',
    icon: '🏫',
    color: '#7c3aed',
  },
  {
    id: 'edu-3',
    year: '2020',
    title: 'Secondary School Certificate (SSC)',
    institution: 'V.K Mate',
    description: [
      'Completed secondary education with strong foundational knowledge.',
    ],
    type: 'education',
    icon: '📚',
    color: 'var(--text-muted)',
  },
];

const Experience: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start center', 'end center'],
  });

  const lineHeight = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  return (
    <section id="experience" className="py-24 relative" style={{ background: 'var(--bg-primary)' }}>
      <div className="absolute inset-0 cyber-grid-bg opacity-30" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10" ref={containerRef}>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20 text-center"
        >
          <p className="section-label-cyber mb-4 justify-center">Journey</p>
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tighter">
            <span style={{ color: 'var(--text-primary)' }}>Experience & </span>
            <span className="gradient-text-cyber">Education</span>
          </h2>
        </motion.div>

        {/* Timeline Container */}
        <div className="relative max-w-5xl mx-auto">
          
          {/* Central Line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] -translate-x-1/2" style={{ background: 'var(--border-subtle)' }}>
            <motion.div 
              className="absolute top-0 left-0 w-full"
              style={{ 
                height: lineHeight, 
                background: 'linear-gradient(180deg, var(--neon-green), var(--neon-cyan))',
                boxShadow: '0 0 10px rgba(0,255,136,0.5)'
              }}
            />
          </div>

          {/* Timeline Items */}
          <div className="space-y-12 md:space-y-24">
            {timelineData.map((item, index) => {
              const isEven = index % 2 === 0;
              return (
                <div key={item.id} className="relative flex flex-col md:flex-row items-center md:justify-between group">
                  
                  {/* Timeline Dot */}
                  <div className="absolute left-4 md:left-1/2 w-8 h-8 md:w-10 md:h-10 -translate-x-1/2 rounded-full flex items-center justify-center z-10 transition-all duration-300"
                    style={{ 
                      background: 'var(--bg-elevated)', 
                      border: `2px solid ${item.color}`,
                      boxShadow: `0 0 15px ${item.color}40`
                    }}
                  >
                    <span className="text-sm md:text-lg">{item.icon}</span>
                  </div>

                  {/* Content Card */}
                  <motion.div
                    initial={{ opacity: 0, x: isEven ? -50 : 50, y: 20 }}
                    whileInView={{ opacity: 1, x: 0, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className={`w-full md:w-[45%] pl-14 md:pl-0 ${isEven ? 'md:pr-12 md:text-right' : 'md:pl-12 md:ml-auto'}`}
                  >
                    <div className="cyber-card p-6 md:p-8 relative overflow-hidden transition-all duration-300 hover:-translate-y-2"
                         style={{ borderTop: `2px solid ${item.color}80` }}>
                      
                      {/* Glow effect on hover */}
                      <div className="absolute top-0 left-0 w-full h-full opacity-0 group-hover:opacity-10 pointer-events-none transition-opacity duration-300"
                           style={{ background: `radial-gradient(circle at ${isEven ? '100%' : '0%'} 0%, ${item.color}, transparent 70%)` }} />

                      <div className={`flex flex-col ${isEven ? 'md:items-end' : 'md:items-start'} mb-4`}>
                        <span className="mono text-xs font-bold px-3 py-1 rounded-full mb-3" 
                              style={{ background: `${item.color}20`, color: item.color, border: `1px solid ${item.color}40` }}>
                          {item.year}
                        </span>
                        <h3 className="text-xl md:text-2xl font-black mb-1" style={{ color: 'var(--text-primary)' }}>
                          {item.title}
                        </h3>
                        <p className="font-medium text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {item.institution}
                        </p>
                      </div>

                      <ul className={`space-y-2 ${isEven ? 'md:text-right' : 'text-left'}`}>
                        {item.description.map((desc, i) => (
                          <li key={i} className={`flex gap-3 text-sm leading-relaxed ${isEven ? 'md:flex-row-reverse' : 'flex-row'}`} style={{ color: 'var(--text-muted)' }}>
                            <span style={{ color: item.color, marginTop: '2px' }}>▹</span>
                            <span className={isEven ? 'md:text-right' : 'text-left'}>{desc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Experience;
